import { Injectable } from '@angular/core';
import { Router, Route } from '@angular/router';

import { environment } from 'environments/environment';

import { NavLink } from '@classes/nav-link';
import { LinkDisplayType } from '@enums/link-display-type.enum';
import { AuthService } from './auth.service';
import { LicenceRoleType } from '@enums/licence-role-type.enum';
import { ProviderType } from '@enums/provider-type.enum';
import { UserService } from './user.service';

@Injectable({
    providedIn: 'root'
})
export class NavService {
    private navLinks: NavLink[] = [
        { route: "/login", icon: "exit_to_app",
            display: LinkDisplayType.unauth
        },
        { route: "/home", icon: "home",
            display: LinkDisplayType.auth
        },
        { route: "/parameters/data-select", icon: "call_merge",
            display: LinkDisplayType.auth
        },
        { route: "/simulation", icon: "play_circle_filled",
            display: LinkDisplayType.auth,
            children: [
                { route: "/simulation/live", icon: "play_circle_filled",
                    display: LinkDisplayType.auth
                },
                { route: "/simulation/advanced", icon: "tune",
                    display: LinkDisplayType.auth,
                    isMobileHidden: true
                },
                { route: "/simulation/investment", icon: "account_balance",
                    display: LinkDisplayType.auth,
                    isMobileHidden: true
                },
            ]
        },
        {
            route: "/dashboardBizzDev", icon: "dashboard",
            display: LinkDisplayType.auth,
            children: [
                {
                    route: "/dashboardBizzDev/overview", icon: "dashboard",
                    display: LinkDisplayType.auth
                },
                {
                    route: "/dashboardBizzDev/advanced-overview", icon: "fact_check",
                    display: LinkDisplayType.auth
                }
            ]
        },
        { route: "/reports", icon: "insert_chart",
            display: LinkDisplayType.auth,
            isMobileHidden: true,
            children: [
                { route: "/reports/simulations", icon: "insert_chart",
                    display: LinkDisplayType.auth,
                    isMobileHidden: true
                },
                { route: "/reports/compare", icon: "compare",
                    display: LinkDisplayType.auth,
                    isMobileHidden: true
                },
                { route: "/reports/system", icon: "insert_chart",
                    display: LinkDisplayType.auth,
                    isMobileHidden: true
                }
            ]
        },
        { route: "/upload", icon: "cloud_upload",
            display: LinkDisplayType.auth,
            isMobileHidden: true
        },
        { route: "/licence", icon: "supervised_user_circle",
            display: LinkDisplayType.auth,
            isMobileHidden: true
        },
        { route: "/licences", icon: "supervised_user_circle",
            display: LinkDisplayType.auth,
            isMobileHidden: true
        }
    ];

    constructor(
        private router: Router,
        private authSrv: AuthService,
        private userSrv: UserService
    ) {
        this.navLinks = this.configureLinks(this.navLinks);
    }


    /**
     * Return hierarchical nav links based on auth status and user role
     * Used in main menu and home page
     * Can be called without any param
     * TODO: Allow filtering
     */
    getNavLinks(links?: NavLink[], isAuth?: boolean): NavLink[] {
        links = links || JSON.parse(JSON.stringify(this.navLinks));
        isAuth = isAuth != null ? isAuth : !!this.authSrv.isAuth();

        return links.filter(link => {
                const isAuthMode: boolean = isAuth ? this.authSrv.isAuthorized(link.roles, link.providers) : false;

                return (isAuthMode && link.display !== LinkDisplayType.unauth)
                    || (!isAuthMode && link.display !== LinkDisplayType.auth);
            })
            .map(link => {
                if(link.children && link.children.length) {
                    link.children = this.getNavLinks(link.children, isAuth);
                    // Remove hierarchy if only one child
                    if(link.children.length === 1) {
                        link = link.children[0];
                    }
                }

                return link;
            });
    }

    getInfoLinks(): NavLink[] {
        let citizenUrl = environment.citizenUrl;
        if (this.userSrv.userParametersSubject.value && this.authSrv.isAuth()) {
            const organism = this.userSrv.userParametersSubject.value.origin.organism.code.replace('commune.', '');
            const language = this.authSrv.getAuthUser().language;
            citizenUrl = `${environment.citizenUrl}/${language}/${organism}`;
        }

        return [
            {
                url: citizenUrl,
                icon: "pie_chart",
                title: "main.nav.links.citizen.shortLabel",
                linkLabel: "main.nav.links.citizen.fullLabel",
                content: "main.nav.links.citizen.summary",
                display: LinkDisplayType.all

            }, {
                route: "/help",
                icon: "contact_support",
                title: "main.nav.links.help.shortLabel",
                linkLabel: "main.nav.links.help.fullLabel",
                content: "main.nav.links.help.summary",
                display: LinkDisplayType.all
            }
        ];
    }

    /**
     * Return NavLink object from array of navigations points
     * Return null if user is not allowed on this route or if the route does'nt exists
     * @param path: array of navigations points from parent to selected child
     * @param icon: code of showed icon
     */
    getLinkFromRoute(path: string[], icon: string): NavLink {
        const routeFound = this.getRouteFromPath(path);
        if(routeFound) {
            const fullPath = '/' + path.join('/');
            return new NavLink(fullPath, routeFound.data.title, icon);
        }

        return null;
    }

    /**
     * Return corresponding route, if allowed, from array of navigations points
     * Check user role and authentication
     * By default, return null if user is not allowed/authenticated or if the route does'nt exists
     * @param path: array of navigations points from parent to selected child
     * @param testRole: if true (default), return null if user role is not allowed
     */
    private getRouteFromPath(path: string[], testRole = true, routes?: Route[], index = 0): Route {
        routes = routes || this.router.config;

        return routes.reduce((found, current) => {
            // If already found in previous loop
            if(found) {
                return found;
            }

            if(current.path === path[index] && (!testRole || this.authSrv.isAuthorized(current.data.roles, current.data.providers))) {
                // If last point of path's array
                if(!path[index + 1]) {
                    return current;
                }

                if(current.children && current.children.length) {
                    return this.getRouteFromPath(path, testRole, current.children, index + 1);
                }
            }

            return null;
        }, null);
    }

    /**
     * Get array of nav links. Generate texts and add roles based on router config
     * Used to manage route texts and role in only one place
     * @param links: NavLink[]
     */
    private configureLinks(links: NavLink[], parentRoles?: LicenceRoleType[], parentProviders?: ProviderType[]): NavLink[] {
        return links.map(link => {
            const splittedRoute = link.route.split('/').slice(1);
            const routeFound = this.getRouteFromPath(splittedRoute, false);

            if (routeFound && routeFound.data) {
                link.roles = routeFound.data.roles || parentRoles || null;
                link.providers = routeFound.data.providers || parentProviders || null;
                link.title = routeFound.data.title + '.shortLabel';
                link.linkLabel = routeFound.data.title + '.fullLabel';
                link.content = routeFound.data.title + '.summary';
            }

            if(link.children) {
                link.children = this.configureLinks(link.children, link.roles, link.providers);
            }

            return link;
        });
    }
}
